'use client';

import React, { useState, useRef, useEffect, useMemo, useCallback, useContext } from 'react';
import { User } from '@supabase/supabase-js';
import { GameType } from '@/lib/types';
import { GameInfo } from '@/components/game-info';
import { GamePlayer } from '@/components/game-player';
import CommentSection from '@/components/CommentSection';
import { RelatedGames } from '@/components/related-games';
import * as Ably from 'ably';
import { useChatClient } from '@ably/chat';
import { ViewCounter } from './view-counter';
import { ClientAdUnit } from '@/components/ads/ClientAdUnit';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Users, Copy } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ChatProviders } from '@/components/chat-providers';
import {
  FacebookShareButton,
  TwitterShareButton,
  FacebookIcon,
  TwitterIcon,
} from 'react-share';
import { getMembership } from '@/lib/membership/membership';
interface GamePageClientProps {
  game: GameType;
  initialUser: User | null;
}

interface ChatComponentProps {
  chatUserId: string | null;
  chatNickname: string | null;
  chatAvatar: string | null;
  roomId: string;
}

// Presence 데이터 구조에 대한 인터페이스 정의
interface ProfilePresenceData {
  profileData?: {
    nickname?: string | null;
    avatar?: string | null;
  };
}

function ChatComponent({ chatUserId, chatNickname, chatAvatar, roomId }: ChatComponentProps) {
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState<Ably.Message[]>([]);
  const [members, setMembers] = useState<Ably.PresenceMessage[]>([]);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const chatClient = useChatClient();

  const findNickname = useCallback((clientId: string | undefined): string => {
    if (!clientId) return '익명';
    if (clientId === chatUserId) return chatNickname || '나';
    const member = members?.find((m: Ably.PresenceMessage) => m.clientId === clientId);
    return member?.data?.profileData?.nickname ?? member?.clientId ?? '익명';
  }, [members, chatUserId, chatNickname]);

  useEffect(() => {
    if (!chatClient || !roomId || !chatUserId) return;

    const channel = chatClient.realtime.channels.get(roomId);

    const enterPresence = async () => {
      try {
        await channel.presence.enter({ 
          profileData: { 
            nickname: chatNickname || '익명',
            avatar: chatAvatar
          } 
        });
        console.log('Entered presence with:', { nickname: chatNickname, avatar: chatAvatar });
      } catch (err) {
        console.error("Error entering presence:", err);
      }
    };

    enterPresence();

    const messageListener = (message: Ably.Message) => {
      console.log('Received raw Ably message:', message);
      setMessages(prev => prev.find(m => m.id === message.id) ? prev : [...prev, message]);
    };

    const presenceListener = (presenceMessage: Ably.PresenceMessage) => {
      console.log('Received presence message:', presenceMessage);
      if (!['enter', 'leave', 'update'].includes(presenceMessage.action)) return;

      setMembers(prev => {
        const existingMemberIndex = prev.findIndex(m => m.clientId === presenceMessage.clientId);

        if (presenceMessage.action === 'leave') {
          console.log(`${presenceMessage.clientId} left`);
          return prev.filter(m => m.clientId !== presenceMessage.clientId);
        } else {
           if (existingMemberIndex !== -1) {
             console.log(`${presenceMessage.clientId} updated presence`);
             const updatedMembers = [...prev];
             updatedMembers[existingMemberIndex] = presenceMessage;
             return updatedMembers;
           } else {
             console.log(`${presenceMessage.clientId} entered`);
             return [...prev, presenceMessage];
           }
        }
      });
    };

    const fetchInitialMembers = async () => {
      try {
        const initialMembers: Ably.PresenceMessage[] = await channel.presence.get();
        console.log('Initial presence members:', initialMembers);
        setMembers(initialMembers);
      } catch (err) {
        console.error("Error fetching initial presence members:", err);
      }
    };

    fetchInitialMembers();

    channel.subscribe('message', messageListener);
    channel.presence.subscribe(['enter', 'leave', 'update'], presenceListener);

    console.log(`Subscribed to messages and presence on channel: ${channel.name}`);

    return () => {
      console.log(`Unsubscribing from messages and presence on channel: ${channel.name}`);
      channel.unsubscribe('message', messageListener);
      channel.presence.unsubscribe(presenceListener);
      channel.presence.leave().catch(err => console.error("Error leaving presence on unsubscribe:", err));
    };
  }, [chatClient, roomId, chatUserId, chatNickname, chatAvatar]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!messageInput.trim() || !chatNickname || !chatClient || !roomId) return;
    try {
      const channel = chatClient.realtime.channels.get(roomId);
      await channel.publish('message', { text: messageInput.trim() });
      console.log('Published message via core SDK');
      setMessageInput("");
    } catch (error) {
      console.error('Error sending message via core SDK:', error);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSend();
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold">게임 채팅</CardTitle>
        <div className="flex items-center text-sm text-muted-foreground">
          <Users className="w-4 h-4 mr-1" />
          <span>{members.length}명 참여중</span>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-72 mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800/50">
          {messages.map((msg, index) => (
            <div key={msg.id || index} className={`flex items-start gap-2 mb-3 ${msg.clientId === chatUserId ? 'justify-end' : 'justify-start'}`}>
              {msg.clientId !== chatUserId && (
                <Avatar className="w-7 h-7 border">
                  <AvatarFallback className="text-xs">{findNickname(msg.clientId)?.slice(0, 1) || '?'}</AvatarFallback>
                </Avatar>
              )}
              <div className={`max-w-[75%] p-2.5 rounded-lg shadow-sm ${msg.clientId === chatUserId
                ? 'bg-orange-500 text-white rounded-br-none'
                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-none'
              }`}>
                {msg.clientId !== chatUserId && (
                  <p className="text-xs font-semibold mb-1 text-orange-600 dark:text-orange-400">{ findNickname(msg.clientId) }</p>
                )}
                <p className="text-sm break-words whitespace-pre-wrap">{(msg.data as any)?.text ?? msg.data ?? ''}</p>
                {msg.timestamp && <p className="text-xs text-right mt-1 opacity-60">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>}
              </div>
              {msg.clientId === chatUserId && (
                <Avatar className="w-7 h-7 border">
                  <AvatarImage src={chatAvatar || undefined} alt={chatNickname || ''} />
                  <AvatarFallback className="text-xs">{chatNickname?.slice(0, 1) || '나'}</AvatarFallback>
                </Avatar>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </ScrollArea>
        <div className="flex items-center gap-2 mt-2">
          <Input
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={chatNickname ? "메시지를 입력하세요..." : "채팅 참여를 위해 로그인 또는 페이지 새로고침"}
            className="flex-1"
            disabled={!chatNickname}
          />
          <Button onClick={handleSend} disabled={!messageInput.trim() || !chatNickname}>
            <Send className="w-4 h-4" />
            <span className="sr-only">보내기</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function GamePageClient({ game, initialUser }: GamePageClientProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(initialUser);
  const [chatUserId, setChatUserId] = useState<string | null>(null);
  const [currentPageUrl, setCurrentPageUrl] = useState('');
  const [membership, setMembership] = useState<any | null>(null);
  useEffect(() => {
    setCurrentUser(initialUser);    
    let id: string | null = null;

    if (initialUser) {
      id = initialUser.id;
    } else {
      const guestId = localStorage.getItem('guestChatId') || `손님_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem('guestChatId', guestId);
      id = guestId;
    }
    setChatUserId(id);
    const membership = getMembership(initialUser?.id || '');
    setMembership(membership);
    // 컴포넌트 마운트 시 현재 URL 설정
    setCurrentPageUrl(window.location.href);
  }, [initialUser]);

  // 링크 복사 함수
  const copyLink = () => {
    navigator.clipboard.writeText(currentPageUrl)
      .then(() => {
        alert('게임 링크가 복사되었습니다!');
      })
      .catch(err => {
        console.error('링크 복사 실패:', err);
        alert('링크 복사에 실패했습니다.');
      });
  };

  if (!chatUserId) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        <span className="ml-2">사용자 정보 로딩 중...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-2">
          <GamePlayer game={game} />
          {/* 공유 버튼 섹션 추가 */} 
          <div className="mt-4 flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">공유하기:</span>
            <FacebookShareButton url={currentPageUrl} title={game.title}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={currentPageUrl} title={game.title}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <button 
              onClick={copyLink} 
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              aria-label="링크 복사"
            >
              <Copy size={16} className="text-gray-700 dark:text-gray-300" />
            </button>
          </div>
          <div className="mt-4">
            <ClientAdUnit membership={membership} adSlot="7840377243" adFormat="auto" />
          </div>
          <ViewCounter gameId={game.id.toString()} currentViews={game.views} />
        </div>
        <div className="lg:col-span-1">
          <Tabs defaultValue="chat" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="chat">채팅</TabsTrigger>
              <TabsTrigger value="info">정보</TabsTrigger>
            </TabsList>
            <TabsContent value="chat">
              <ChatProviders clientId={chatUserId} roomId={game.id.toString()} />
            </TabsContent>
            <TabsContent value="info">
              <GameInfo game={game} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <CommentSection postId={game.id.toString()} userId={currentUser?.id || ""} />

      <RelatedGames currentGameId={game.id.toString()} />
    </div>
  );
} 